/**
 * A returning person on this device: the account it last signed in with, and one button. "Continue" takes the
 * same way as last time — the passkey prompt, the company's SSO, or a fresh code — and the person never types the
 * address again. Another account, or forgetting this one on a shared computer, is one tap away.
 */
import { Icon, Spinner } from '@evt/flow-stream-ui'
import type { RememberedAccount } from '../../auth/remembered-account'
import { useI18n } from '../../i18n'

export function AccountStep({
  account,
  busy,
  onContinue,
  onCodeInstead,
  showCodeFallback,
  onOtherAccount,
  onForget
}: {
  account: RememberedAccount
  busy: boolean
  onContinue: () => void
  /** After a passkey attempt that did not work: the code always does. */
  onCodeInstead: () => void
  showCodeFallback: boolean
  onOtherAccount: () => void
  onForget: () => void
}) {
  const { t } = useI18n()
  const via =
    account.method === 'sso' && account.sso
      ? t.login.rememberedVia.sso(account.sso.label)
      : account.method === 'passkey'
        ? t.login.rememberedVia.passkey
        : t.login.rememberedVia.code
  return (
    <div className="login-body">
      <div className="login-account">
        <span className="login-avatar" aria-hidden="true">
          {account.email.charAt(0).toUpperCase()}
        </span>
        <span className="login-account-text">
          <strong className="login-account-email" title={account.email}>
            {account.email}
          </strong>
          <span className="muted">{via}</span>
        </span>
      </div>
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={onContinue}>
        {busy ? <Spinner size={14} /> : account.method === 'passkey' ? <Icon name="shield" size={15} /> : null}{' '}
        {t.login.continueAs}
      </button>
      {showCodeFallback && (
        <button type="button" className="btn btn-block" disabled={busy} onClick={onCodeInstead}>
          {t.login.passkeyFallback}
        </button>
      )}
      <div className="login-row">
        <button type="button" className="login-link" disabled={busy} onClick={onOtherAccount}>
          {t.login.useAnotherAccount}
        </button>
        <button type="button" className="login-link login-link-quiet" disabled={busy} onClick={onForget}>
          {t.login.forgetAccount}
        </button>
      </div>
    </div>
  )
}
